import { useQuery } from '@tanstack/react-query'
import keyBy from 'lodash/keyBy'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { FormTemplatesHttpClient } from '@omnea-app/form-templates/formTemplatesHttpClient'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'

interface Props<TData = FormQuestionInsightCountByFormTemplate> {
  enabled?: boolean
  select?: (data: FormQuestionInsightCountByFormTemplate) => TData
}

export type FormQuestionInsightCountByFormTemplate = Record<string, { formTemplateId: string; count: number }>
export const buildGetFormQuestionInsightCountByFormTemplateQueryKey = () => ['form-insight-count-by-form-template']

const THIRTY_MINUTES_IN_MS = 1000 * 60 * 30

export const useGetFormQuestionInsightCountByFormTemplate = <TData = FormQuestionInsightCountByFormTemplate>({
  enabled = true,
  select,
}: Props<TData> = {}) => {
  const { getToken } = useAuthenticatedUser()
  const { FORM_INSIGHTS_AI } = useFeatureFlags()

  return useQuery({
    enabled: FORM_INSIGHTS_AI && enabled,
    queryKey: buildGetFormQuestionInsightCountByFormTemplateQueryKey(),
    queryFn: async () => {
      const authorization = await getToken()
      const client = new FormTemplatesHttpClient({ authorization })

      const { data, error } = await client.GET('/insights/questions', {})
      throwIfApiError(error)
      return keyBy(data.byFormTemplate, 'formTemplateId')
    },
    staleTime: THIRTY_MINUTES_IN_MS,
    select,
  })
}

const selectHasAnyInsights = (data: FormQuestionInsightCountByFormTemplate) => {
  return Object.values(data).some((insightCount) => insightCount.count > 0)
}

export const useHasAnyFormQuestionInsights = () => {
  return !!useGetFormQuestionInsightCountByFormTemplate({ select: selectHasAnyInsights }).data
}
